/**
 * Single source of truth for Online Ordering customer auth in localStorage.
 * Always encrypts on write and decrypts on read (plain JSON still accepted once).
 */

import {
  decryptStorageValue,
  encryptStorageValue,
} from "./storageCipher";
import {
  getRestaurantStorageItem,
  setRestaurantStorageItem,
  removeRestaurantStorageItem,
  getRestaurantStorageKey,
} from "./restaurantStorageUtils";

export const CUSTOMER_AUTH_BASE_KEY = "customerAuth";
export const LEGACY_CUSTOMER_STORAGE_KEY = "onlineOrdering_customerAuth";

export function getCustomerAuthStorageKey(restaurantSlug) {
  if (!restaurantSlug) return LEGACY_CUSTOMER_STORAGE_KEY;
  return getRestaurantStorageKey(restaurantSlug, CUSTOMER_AUTH_BASE_KEY);
}

/**
 * @returns {{ customerToken: string|null, customerRefreshToken: string|null, customerUser: object|null } | null}
 */
export function readCustomerAuthStorage(restaurantSlug) {
  try {
    if (restaurantSlug) {
      const scoped = getRestaurantStorageItem(restaurantSlug, CUSTOMER_AUTH_BASE_KEY);
      if (
        scoped &&
        (scoped.customerToken || scoped.token || scoped.customerRefreshToken || scoped.refreshToken) &&
        (scoped.customerUser || scoped.customerToken || scoped.token)
      ) {
        return normalizeCustomerAuth(scoped);
      }
    }

    const rawLegacy = localStorage.getItem(LEGACY_CUSTOMER_STORAGE_KEY);
    if (!rawLegacy) return null;
    const legacy = decryptStorageValue(rawLegacy);
    if (!legacy) return null;

    const normalized = normalizeCustomerAuth(legacy);
    if (restaurantSlug && normalized) {
      // Migrate legacy → scoped encrypted key
      writeCustomerAuthStorage(restaurantSlug, normalized);
      try {
        localStorage.removeItem(LEGACY_CUSTOMER_STORAGE_KEY);
      } catch {
        /* ignore */
      }
    }
    return normalized;
  } catch {
    return null;
  }
}

export function writeCustomerAuthStorage(
  restaurantSlug,
  { customerToken, customerRefreshToken, customerUser } = {}
) {
  const payload = {
    customerToken: customerToken || null,
    customerRefreshToken: customerRefreshToken || null,
    customerUser: customerUser || null,
  };
  try {
    if (restaurantSlug) {
      setRestaurantStorageItem(restaurantSlug, CUSTOMER_AUTH_BASE_KEY, payload);
    } else {
      localStorage.setItem(LEGACY_CUSTOMER_STORAGE_KEY, encryptStorageValue(payload));
    }
  } catch {
    /* ignore */
  }
  return payload;
}

export function clearCustomerAuthStorage(restaurantSlug) {
  try {
    if (restaurantSlug) {
      removeRestaurantStorageItem(restaurantSlug, CUSTOMER_AUTH_BASE_KEY);
    }
    localStorage.removeItem(LEGACY_CUSTOMER_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function getCustomerAccessToken(restaurantSlug) {
  const data = readCustomerAuthStorage(restaurantSlug);
  return data?.customerToken || data?.token || "";
}

function normalizeCustomerAuth(raw) {
  if (!raw || typeof raw !== "object") return null;
  return {
    customerToken: raw.customerToken ?? raw.token ?? null,
    customerRefreshToken: raw.customerRefreshToken ?? raw.refreshToken ?? null,
    customerUser: raw.customerUser ?? null,
    // Keep aliases so older callers still work
    token: raw.customerToken ?? raw.token ?? null,
    refreshToken: raw.customerRefreshToken ?? raw.refreshToken ?? null,
  };
}
